import React, { useState, useContext, useEffect } from "react";
import AccreditationInput from "../../AccreditationInput/AccreditationInput";
import { createData } from "../../../utils/methods";
import { useNavigate } from "react-router-dom";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import { trimValues } from "../../../utils/trimValues";
import { useAlert } from "../../../utils/AlertContext";
import { apiRoutes } from "../../../utils/apiRoutes";
import HistoryPanel from "../../HistoryPanel/HistoryPanel";
import EditModalShell from "./EditModalShell";
import RegisteredByLine from "./RegisteredByLine";
import useEnterKeySubmit from "../../../hooks/useEnterKeySubmit";

// Build-time literal (unlike apiRoutes) so Terser can prove the History
// branch below is dead code on tiers other than Third — genuinely absent
// from the compiled bundle, not just unrendered. See AddPerson.jsx.
const TIER = process.env.REACT_APP_MODE;

const AddVehicle = ({
  onCloseClick,
  object,
  editing,
  eventId,
  dogadjajGrupaId,
  handleAddData,
  handleUpdateData,
}) => {
  const { t } = useContext(TranslationsContext);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  // Form state
  const [formData, setFormData] = useState({
    tip: "",
    registracija: "",
    entitet: "",
    marka: "",
    model: "",
    dogadjajId: eventId,
    dogadjajGrupaId: dogadjajGrupaId || "",
  });

  // Auto-detect user's group if not provided
  useEffect(() => {
    if (!dogadjajGrupaId && eventId) {
      // For regular users, we could fetch their group membership
      // This would require an API call like:
      // GET /users/me/groups?eventId=${eventId}
      // or GET /events/${eventId}/my-group
      // For now, we'll leave it empty and let the backend handle it
      console.log("No group specified, backend will determine user's group");
    }
  }, [dogadjajGrupaId, eventId]);

  // Vehicle types based on enum TipVozila
  const vehicleTypes = [
    { key: "autobus", value: t("AUTOBUS") },
    { key: "kamion", value: t("KAMION") },
    { key: "kombi", value: t("KOMBI") },
    { key: "pick_up", value: t("PICK_UP") },
    { key: "automobil", value: t("AUTOMOBIL") },
    { key: "motor", value: t("MOTOR") },
  ];

  const [selectedOption, setSelectedOption] = useState(null);

  const handleInputChange = (field, value) => {
    // Convert registration to uppercase
    const processedValue =
      field === "registracija" ? value.toUpperCase() : value;

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handleSaveClick = async () => {
    // Basic validation
    if (!formData.tip.trim()) {
      showAlert(t("VEHICLE_TYPE_REQUIRED"), "error");
      return;
    }
    if (!formData.registracija.trim()) {
      showAlert(t("REGISTRATION_REQUIRED"), "error");
      return;
    }
    if (!formData.entitet.trim()) {
      showAlert(t("ENTITY_REQUIRED"), "error");
      return;
    }
    if (!formData.marka.trim()) {
      showAlert(t("BRAND_REQUIRED"), "error");
      return;
    }
    if (!formData.model.trim()) {
      showAlert(t("MODEL_REQUIRED"), "error");
      return;
    }

    setIsLoading(true);

    try {
      const trimmedData = trimValues(formData);

      if (editing) {
        // For editing, call the parent's update handler
        await handleUpdateData({ ...trimmedData, id: object.id });
        onCloseClick();
      } else {
        // For adding, call the parent's add handler
        await handleAddData(trimmedData);
        onCloseClick();
      }
    } catch (error) {
      console.error("Error saving vehicle:", error);
      showAlert(t("ERROR_SAVING_VEHICLE"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEnterKeySubmit(handleSaveClick);

  // Populate form data when editing
  useEffect(() => {
    if (editing && object && object.tip) {
      setFormData({
        tip: object.tip || "",
        registracija: object.registracija || "",
        entitet: object.entitet || "",
        marka: object.marka || "",
        model: object.model || "",
        dogadjajId: eventId,
        dogadjajGrupaId: object.dogadjajGrupaId || dogadjajGrupaId || "",
      });

      // Set selected option for dropdown
      setSelectedOption(object.tip || "");
    }
  }, [editing, object, eventId, dogadjajGrupaId]);

  return (
    <EditModalShell
      isLoading={isLoading}
      t={t}
      headerInfo={
        editing && object?.korisnik ? (
          <RegisteredByLine korisnik={object.korisnik} t={t} />
        ) : (
          <p className="inria-sans-400 font-24 modal-title">
            {editing ? t("EDIT_VEHICLE") : t("ADD_VEHICLE")}
          </p>
        )
      }
      onSave={handleSaveClick}
      onClose={onCloseClick}
      sidePanel={
        editing && TIER === "third" ? (
          <HistoryPanel tabela="vozila" redId={object?.id} />
        ) : null
      }
    >
      <div className="add-vehicle-modal">
        <div className="flex-direction-column gap-24 width-100">
          {/* Vehicle Type */}
          <div className="width-100">
            <AccreditationInput
              label={t("VEHICLE_TYPE")}
              className="modal-input"
              type="dropdown"
              options={vehicleTypes}
              selectedOption={selectedOption}
              setSelectedOption={(option) => {
                setSelectedOption(option);
                handleInputChange("tip", option || "");
              }}
              required
            />
          </div>

          {/* Registration */}
          <div className="width-100">
            <AccreditationInput
              label={t("REGISTRATION")}
              className="modal-input"
              setInputValue={(value) =>
                handleInputChange("registracija", value)
              }
              value={formData.registracija}
              required
            />
          </div>

          {/* Entity */}
          <div className="width-100">
            <AccreditationInput
              label={t("ENTITY")}
              className="modal-input"
              setInputValue={(value) => handleInputChange("entitet", value)}
              value={formData.entitet}
              required
            />
          </div>

          {/* Brand */}
          <div className="width-100">
            <AccreditationInput
              label={t("BRAND")}
              className="modal-input"
              setInputValue={(value) => handleInputChange("marka", value)}
              value={formData.marka}
              required
            />
          </div>

          {/* Model */}
          <div className="width-100">
            <AccreditationInput
              label={t("MODEL")}
              className="modal-input"
              setInputValue={(value) => handleInputChange("model", value)}
              value={formData.model}
              required
            />
          </div>

          {/* Event Group - Hidden field, automatically set from activeGroup */}
          <div style={{ display: "none" }}>
            <AccreditationInput
              label={t("EVENT_GROUP")}
              className="modal-input"
              setInputValue={(value) =>
                handleInputChange("dogadjajGrupaId", value)
              }
              value={formData.dogadjajGrupaId}
            />
          </div>
        </div>
      </div>
    </EditModalShell>
  );
};

export default AddVehicle;
